import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export type McpScope = 'agents:invoke' | 'agents:write' | 'agents:publish';

export interface McpToken {
  tokenId: string;
  grantId?: string;
  name: string;
  orgId: string;
  userId: string;
  clientId?: string;
  applicationId?: string;
  appIds?: string[];
  tokenSuffix: string;
  scopes: McpScope[];
  capabilities?: McpScope[];
  status: 'active' | 'revoked' | 'expired';
  createdFrom?: string;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string | null;
  token?: string;
}

export interface McpAuditEvent {
  auditId: string;
  tokenId: string;
  toolName?: string | null;
  operation: string;
  resultStatus: string;
  agentName?: string | null;
  createdAt?: string | null;
  errorCode?: string | null;
}

export interface CreateMcpTokenRequest {
  name: string;
  applicationId?: string;
  scopes: McpScope[];
}

export interface OauthConsentRequest {
  orgId: string;
  clientId: string;
  redirectUri: string;
  state?: string;
  codeChallenge: string;
  codeChallengeMethod?: string;
  capabilities: McpScope[];
  appIds?: string[];
  name?: string;
}

export interface OauthConsentResponse {
  grantId: string;
  redirectUrl: string;
}

export const mcpAccessApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMcpTokens: builder.query<McpToken[], void>({
      query: () => '/portal/mcp/tokens',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ tokens: McpToken[]; grants: McpToken[] }>(
          response,
        );
        const rows = normalized.grants ?? normalized.tokens ?? [];
        return rows.map((row) => ({
          ...row,
          tokenId: row.tokenId || row.grantId || '',
          grantId: row.grantId || row.tokenId,
          scopes: row.scopes?.length ? row.scopes : row.capabilities || [],
        }));
      },
      providesTags: ['McpToken'],
    }),

    createMcpToken: builder.mutation<McpToken, CreateMcpTokenRequest>({
      query: (body) => ({
        url: '/portal/mcp/tokens',
        method: 'POST',
        body: {
          name: body.name,
          application_id: body.applicationId || undefined,
          scopes: body.scopes,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<McpToken>(response),
      invalidatesTags: ['McpToken'],
    }),

    revokeMcpToken: builder.mutation<{ tokenId: string; status: string }, string>({
      query: (tokenId) => ({
        url: `/portal/mcp/tokens/${tokenId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['McpToken', 'McpAudit'],
    }),

    getMcpAudit: builder.query<McpAuditEvent[], void>({
      query: () => '/portal/mcp/audit?limit=25',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ events: McpAuditEvent[] }>(response);
        return normalized.events ?? [];
      },
      providesTags: ['McpAudit'],
    }),

    submitOauthConsent: builder.mutation<OauthConsentResponse, OauthConsentRequest>({
      query: (body) => ({
        url: '/portal/oauth/consent',
        method: 'POST',
        body: {
          org_id: body.orgId,
          client_id: body.clientId,
          redirect_uri: body.redirectUri,
          state: body.state,
          code_challenge: body.codeChallenge,
          code_challenge_method: body.codeChallengeMethod || 'S256',
          capabilities: body.capabilities,
          app_ids: body.appIds || [],
          name: body.name,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OauthConsentResponse>(response),
      invalidatesTags: ['McpToken'],
    }),
  }),
});

export const {
  useGetMcpTokensQuery,
  useCreateMcpTokenMutation,
  useRevokeMcpTokenMutation,
  useGetMcpAuditQuery,
  useSubmitOauthConsentMutation,
} = mcpAccessApi;
